import { blog_card } from "@core/gql-mockups/blogs";
import { filters } from "@core/gql-mockups/filters";
import { pagination } from "@core/gql-mockups/pagination";
import {
  favorite_products,
  product_card,
  product_page,
  related_products,
} from "@core/gql-mockups/products";
import { media_with_details } from "@core/gql-mockups/media";

const { minimize_str } = require("@core/helpers/string");

export const GET_PRODUCT_BY_SLUG = ({ product_page_mockup }) =>
  minimize_str(`
  query ($slug: String, $type:Int) {
    result: getProductBySlug(slug: $slug, type:$type) ${product_page_mockup || product_page
    }
  }
`);

export const GET_PRODUCTS = ({ product_card_mockup }) =>
  minimize_str(`
  query(
    $filter: ProductFilter
    $page: Int
    $limit: Int
    $sort: String
    $sort_by_in_stock: Boolean
    $include_sub_price_in_stock: Boolean
    $sort_by_variant_id: ID
  ) {
    result: getProducts(
      filter: $filter
      page: $page
      limit: $limit
      sort: $sort
      sort_by_in_stock: $sort_by_in_stock
      include_sub_price_in_stock: $include_sub_price_in_stock
      sort_by_variant_id: $sort_by_variant_id
    ){
      paginate {
        total
        pages
        page
        limit
      }
      data ${product_card_mockup || product_card}
    }
  }
`);

/**
 * `include_filters` / `include_products` / `include_blogs` (default true) leave a part out of the selection: a blog
 * listing reads only `sorts` and `blogs`, and the whole `filters` tree (nested category options) came back on every
 * request (chawk-bazar / ciseco sent their own query through `gql` for that).
 * `include_related_links` (default false) adds the shortcuts the admin sets above the filters of a page; jument asked
 * for it, off by default so a site on an older backend without the field keeps the same query.
 */
export const GET_PRODUCTS_FILTERS = ({
  custom_product_card_mockup,
  custom_blog_card_mockup,
  include_filters = true,
  include_products = true,
  include_blogs = true,
  include_related_links = false,
}) =>
  minimize_str(`
  query(
    # $type: String # deprecated
    $model_name: String
    $model_type: Int
    $target_model_name: String
    $target_model_type: Int
    $slug: String
    $query_filter: JSON
    $query_sort: String
    $result_type: Int
    $page: Int
    $limit: Int
    $with_products: Boolean
    $include_sub_price_in_stock: Boolean
  ) {
    result: getProductsFilters(
      # type: $type, # deprecated
      model_name: $model_name,
      model_type: $model_type,
      target_model_name: $target_model_name,
      target_model_type: $target_model_type,
      slug: $slug,
      with_products: $with_products,
      query_filter: $query_filter,
      query_sort: $query_sort,
      result_type: $result_type,
      page: $page,
      limit: $limit,
      include_sub_price_in_stock: $include_sub_price_in_stock
    ) {
      filter_group_id
      sorts {
        title
        key
      }
      ${include_filters ? `filters ${filters}` : ""}
      sort_by_in_stock
      sort_by_variant_id
      ${include_related_links ? `related_links {
        title
        link
      }` : ""}
      ${include_products ? `products {
        paginate ${pagination}
        data ${custom_product_card_mockup || product_card}
      }` : ""}
      ${include_blogs ? `blogs {
        paginate ${pagination}
        data ${custom_blog_card_mockup || blog_card}
      }` : ""}
    }
  }
`);

export const GET_RELATED_PRODUCTS = ({ product_card_mockup }) =>
  minimize_str(`
  query ($slug: String, $type:Int) {
    result: getProductBySlug(slug: $slug, type:$type) ${product_card_mockup || related_products
    }
  }
`);

export const GET_FAVORITE_PRODUCTS = (custom_gql) => minimize_str(`
  query($page: Int, $limit: Int) {
   result: getFavoriteProducts(page: $page, limit: $limit) {
      paginate ${pagination}
      data ${custom_gql || favorite_products}
    }
  }
`);

export const GET_SEARCH_PRODUCTS = minimize_str(`
  query (
    $page: Int,
    $limit: Int,
    $sort_by_in_stock: Boolean
    $search: String,
    $product_type: Int
    $category_type: Int
    ) {
    result: getProductsSearch(page: $page, product_type: $product_type, category_type: $category_type limit: $limit, search: $search, sort_by_in_stock: $sort_by_in_stock) {
      products {
        id
        title
        title_en
        media {
          url
          alt
        }
        seo_full_url
        is_in_stock
      }
      brands {
        id
        title
        title_en
        seo_full_url
      }
      categories {
        id
        title
        seo_full_url
      }
      
    }
  }
`);

export const GET_PRODUCT_SLUG_BY_SKU = minimize_str(`
  query ($sku: String, $type: Int) {
    result: getProductSlugWithSku(sku: $sku, type: $type)
  }
`);

export const GET_PRODUCTS_LITE = (custom_mockup) =>
  minimize_str(`
  query  ($page: Int, $limit: Int, $filter: ProductFilterLite, $sort: String, $sort_by_in_stock: Boolean){
    result: getProductsLite(page: $page, limit: $limit, filter: $filter, sort: $sort, sort_by_in_stock: $sort_by_in_stock) {
      paginate {
        total
        pages
        page
        limit
      }
      data ${custom_mockup || product_card}
    }
  }
`);

export const GET_PRODUCTS_OPTIMIZED = (custom_mockup) =>
  minimize_str(`
  query  ($page: Int, $limit: Int, $filter: ProductFilter, $sort: String, $sort_by_in_stock: Boolean){
    result: getProductsOptimized(page: $page, limit: $limit, filter: $filter, sort: $sort, sort_by_in_stock: $sort_by_in_stock) {
      paginate {
        total
        pages
        page
        limit
      }
      data ${custom_mockup || product_card}
    }
  }
`);

export const GET_SUGGESTION_PRODUCTS_IN_CART = minimize_str(`
  query (
    $products: [SuggestionProductInput]
    $limit: Int
  ) {
    data: getSuggestionProducts(
      products: $products
      limit: $limit
    ) {
      id
      title
      media ${media_with_details}
      seo {
        url
      }
      mix_variant {
        media {
          url
        }
         price {
        price
        offer_price
        main_price
        discount_percent
        discount_type
        show_discount_timer
        has_offer
      }
      details {
        count
      }
      }     
    }
  }
`);
